import { useContext, useState } from "react";
import { observer } from "mobx-react-lite";

import { ParametersPreviewContent } from "./AdvancedParameters/ParametersPreview";
import { ParametersContent } from "./ParametersContent/ParametersContent";
import { ParametersTrigger } from "./ParametersTrigger";
import { ParameterProvider } from "./store";
import { ModelParametersProps } from "./types";

import LoadingSpinner from "@/components/LoadingSpinner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ModelsStoreProvider } from "@/providers/ProviderAndModelsProvider/models-store";
import { PlaygroundStoreContext } from "@/components/Playground/playground-store";
import Functions from "@/components/PromptBlueprint/components/Functions";

const PopoverParametersView = ({
  open,
  setOpen,
  triggerLabel,
  triggerClassName,
  triggerLabelClassName,
  triggerLogo,
  triggerVariant,
  buttonSize,
  showIcon,
  showTooltip,
  readonly,
  showLearnMore,
  titleClassName,
  showGeneralInformation,
  shouldShowClearButton,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerLabel?: string;
  triggerClassName?: string;
  triggerLabelClassName?: string;
  triggerLogo?: React.ReactNode;
  triggerVariant?: "default" | "ghost";
  buttonSize?: "default" | "sm";
  showIcon?: boolean;
  showTooltip?: boolean;
  readonly: boolean;
  showLearnMore: boolean;
  titleClassName?: string;
  showGeneralInformation: boolean;
  shouldShowClearButton: boolean;
}) => (
  <Popover open={open} onOpenChange={setOpen} modal>
    <PopoverTrigger
      onClick={e => {
        e.stopPropagation();
      }}
      asChild
    >
      <ParametersTrigger
        triggerLabel={triggerLabel}
        triggerClassName={triggerClassName}
        triggerLabelClassName={triggerLabelClassName}
        triggerLogo={triggerLogo}
        triggerVariant={triggerVariant}
        buttonSize={buttonSize}
        showIcon={showIcon}
        showTooltip={showTooltip}
      />
    </PopoverTrigger>
    <PopoverContent
      onClick={e => {
        e.stopPropagation();
      }}
      className={
        readonly
          ? "w-[450px] overflow-visible p-4 transition-all duration-300 ease-in-out"
          : "w-full max-w-[527px] transition-all duration-300 ease-in-out"
      }
    >
      {readonly ? (
        <ParametersPreviewContent
          showLearnMore={showLearnMore}
          titleClassName={titleClassName}
          showGeneralInformation={showGeneralInformation}
        />
      ) : (
        <ParametersContent shouldShowClearButton={shouldShowClearButton} />
      )}
    </PopoverContent>
  </Popover>
);

const InlineParametersView = ({
  readonly,
  showLearnMore,
  titleClassName,
  showGeneralInformation,
  shouldShowClearButton,
}: {
  readonly: boolean;
  showLearnMore: boolean;
  titleClassName?: string;
  showGeneralInformation: boolean;
  shouldShowClearButton: boolean;
}) => {
  if (readonly) {
    return (
      <ParametersPreviewContent
        showLearnMore={showLearnMore}
        titleClassName={titleClassName}
        showGeneralInformation={showGeneralInformation}
      />
    );
  }

  return <ParametersContent shouldShowClearButton={shouldShowClearButton} />;
};

export const ModelParameters = observer(
  ({
    promptData,
    className,
    onUpdate,
    isPromptChat,
    onModeChange,
    displayAsPopover = true,
    shouldShowClearButton = true,
    readonly = false,
    showLearnMore = true,
    titleClassName,
    triggerLabel,
    triggerClassName,
    triggerLabelClassName,
    triggerLogo,
    triggerVariant = "ghost",
    buttonSize = "sm",
    showIcon = true,
    showTooltip = true,
    showGeneralInformation = true,
    messages,
    functionDialogTriggerVariant,
    controlledOpen,
    onControlledOpenChange,
  }: ModelParametersProps) => {
    const [internalOpen, setInternalOpen] = useState(false);
    const open = controlledOpen ?? internalOpen;
    const setOpen = onControlledOpenChange ?? setInternalOpen;
    const playground = useContext(PlaygroundStoreContext);

    if (!promptData) {
      return (
        <div className="flex h-full w-[132px] items-center justify-center">
          <LoadingSpinner size={4} />
        </div>
      );
    }

    return (
      <ModelsStoreProvider>
        <ParameterProvider
          displayAsPopover={displayAsPopover}
          isPlayground={!!playground}
          promptData={promptData}
          isPromptChat={isPromptChat}
          onModeChange={onModeChange}
          isOpen={open}
          onUpdate={onUpdate}
          messages={messages}
        >
          <div className={`flex ${className} gap-2`}>
            {displayAsPopover ? (
              <PopoverParametersView
                open={open}
                setOpen={setOpen}
                triggerLabel={triggerLabel}
                triggerClassName={triggerClassName}
                triggerLabelClassName={triggerLabelClassName}
                triggerLogo={triggerLogo}
                triggerVariant={triggerVariant as "default" | "ghost" | undefined}
                buttonSize={buttonSize as "default" | "sm"}
                showIcon={showIcon}
                showTooltip={showTooltip}
                readonly={readonly}
                showLearnMore={showLearnMore}
                titleClassName={titleClassName}
                showGeneralInformation={showGeneralInformation}
                shouldShowClearButton={shouldShowClearButton}
              />
            ) : (
              <InlineParametersView
                readonly={readonly}
                showLearnMore={showLearnMore}
                titleClassName={titleClassName}
                showGeneralInformation={showGeneralInformation}
                shouldShowClearButton={shouldShowClearButton}
              />
            )}
          </div>
          {playground && (
            <Functions
              readonly={playground?.readonly}
              triggerVariant={functionDialogTriggerVariant}
            />
          )}
        </ParameterProvider>
      </ModelsStoreProvider>
    );
  },
);
